import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Activity, Eye, Upload, MousePointer, Users, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function ActivityTracking() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState({ logs: [], total: 0, page: 1, pages: 1 });
  const [logType, setLogType] = useState('all');
  const [page, setPage] = useState(1);

  const load = useCallback(async (p = 1) => {
    try {
      const [s, l] = await Promise.all([
        api.get('/admin/activity-stats'),
        api.get(`/admin/activity-log?page=${p}&limit=30&log_type=${logType === 'all' ? '' : logType}`),
      ]);
      setStats(s.data);
      setLogs(l.data);
      setPage(p);
    } catch (e) { console.error(e); }
  }, [logType]);

  useEffect(() => { if (user?.role === 'admin') load(1); }, [load, user]);

  if (user?.role !== 'admin') {
    return (
      <div className="max-w-[1200px] mx-auto px-4 py-12 text-center">
        <Shield className="w-12 h-12 text-ink/15 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-extrabold text-ink mb-2">Admin Access Required</h1>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 space-y-5" data-testid="activity-tracking">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink flex items-center gap-2">
        <Activity className="w-6 h-6 text-brand-orange" strokeWidth={1.5} /> Usage & Activity Tracking
      </h1>

      {/* Stats */}
      {stats && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Page Visits', value: stats.total_visits, icon: Eye, color: 'text-info-blue' },
              { label: 'Actions', value: stats.total_actions, icon: MousePointer, color: 'text-brand-orange' },
              { label: 'Uploads', value: stats.total_uploads, icon: Upload, color: 'text-ai-purple' },
              { label: 'Unique Users', value: stats.unique_users, icon: Users, color: 'text-status-green' },
            ].map((s, i) => (
              <div key={i} className="solid-card p-4 text-center" data-testid={`activity-stat-${i}`}>
                <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} strokeWidth={1.5} />
                <span className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/40 block">{s.label}</span>
                <span className={`font-display text-2xl font-extrabold ${s.color}`}>{s.value?.toLocaleString()}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Page visits chart */}
            {stats.page_visits?.length > 0 && (
              <div className="solid-card p-5">
                <h3 className="font-display text-lg font-extrabold text-ink mb-3">Page Visits</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stats.page_visits} layout="vertical" barSize={14}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis type="number" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
                    <YAxis dataKey="page" type="category" width={100} tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 9 }} />
                    <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 4 }} />
                    <Bar dataKey="count" fill="#1A4D8A" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Upload stats */}
            <div className="solid-card p-5">
              <h3 className="font-display text-lg font-extrabold text-ink mb-3">Document Uploads</h3>
              {stats.uploads?.length > 0 ? (
                <div className="space-y-3">
                  {stats.uploads.map((u, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-[4px] bg-ink/3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-ai-purple" strokeWidth={1.5} />
                        <span className="font-body text-sm text-ink">{(u.type || 'unknown').toUpperCase()}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-display text-lg font-extrabold text-ink">{u.count}</span>
                        <span className="font-body text-xs text-ink/40 block">{u.records?.toLocaleString()} records</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="font-body text-sm text-ink/40 text-center py-6">No uploads yet</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Activity Log */}
      <div className="flex items-center gap-3">
        <h3 className="font-display text-lg font-extrabold text-ink">Activity Log</h3>
        <Select value={logType} onValueChange={setLogType}>
          <SelectTrigger className="w-[150px] font-body text-xs h-8" data-testid="activity-log-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="page_visit">Page Visits</SelectItem>
            <SelectItem value="action">Actions</SelectItem>
          </SelectContent>
        </Select>
        <span className="font-body text-xs text-ink/40">{logs.total} entries</span>
      </div>

      <div className="solid-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-ink/3">
            <tr>
              <th className="text-left p-2 font-body text-[10px] uppercase tracking-wider text-ink/40">Time</th>
              <th className="text-left p-2 font-body text-[10px] uppercase tracking-wider text-ink/40">Type</th>
              <th className="text-left p-2 font-body text-[10px] uppercase tracking-wider text-ink/40">Detail</th>
              <th className="text-left p-2 font-body text-[10px] uppercase tracking-wider text-ink/40">User</th>
              <th className="text-left p-2 font-body text-[10px] uppercase tracking-wider text-ink/40">Role</th>
            </tr>
          </thead>
          <tbody>
            {logs.logs?.map((log, i) => (
              <tr key={i} className="border-t border-ink/5">
                <td className="p-2 font-body text-[11px] text-ink/50">{new Date(log.timestamp).toLocaleString()}</td>
                <td className="p-2">
                  <span className={`text-[9px] font-body px-1.5 py-0.5 rounded-[4px] uppercase tracking-wider ${log.type === 'page_visit' ? 'bg-info-blue/10 text-info-blue' : 'bg-brand-orange/10 text-brand-orange'}`}>{log.type === 'page_visit' ? 'Visit' : 'Action'}</span>
                </td>
                <td className="p-2 font-body text-[11px] text-ink">{log.page || log.action}{log.detail ? `: ${log.detail}` : ''}</td>
                <td className="p-2 font-body text-[11px] text-ink/50">{log.user_email || 'anonymous'}</td>
                <td className="p-2 font-body text-[10px] text-ink/40">{log.user_role || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {logs.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)} className="font-body text-xs rounded-[4px] h-7">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <span className="font-body text-xs text-ink/40">Page {page}/{logs.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= logs.pages} onClick={() => load(page + 1)} className="font-body text-xs rounded-[4px] h-7">
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
